/*
 * Product pack selector — Single / 2-pack / 3-pack with a colour per item and one shared size.
 * Extends VariantSelects (product-information-bundle.js, loaded first) only to reuse its colour-driven gallery.
 * Pack pricing is applied at checkout by Shopify automatic discounts; data-pack-*-discount mirrors them for display.
 */

const PACK_MAX_PER_VARIANT = 5;

class ProductPackSelector extends (window.VariantSelects || HTMLElement) {
  connectedCallback() {
    this.productData = this.getPackProductData();
    if (!this.productData) return;

    this.colorIndex = this.productData.options.indexOf("Color");
    this.sizeIndex = this.productData.options.indexOf("Size");
    this.packSize = Number(this.dataset.defaultPackSize) || 1;
    this.discountType = ["fixed", "pack_fixed"].includes(this.dataset.discountType)
      ? this.dataset.discountType
      : "percentage";
    this.discounts = {
      1: 0,
      // dataset can't camel-case "-2", so read the attributes directly
      2: Number(this.getAttribute("data-pack-2-discount")) || 0,
      3: Number(this.getAttribute("data-pack-3-discount")) || 0,
    };
    this.selectedSize = null;
    this.loading = false;
    this.galleryRowIndex = 0;

    this.productInfo = this.closest(".product-information");
    this.rows = Array.from(this.querySelectorAll("[data-pack-colour-row]"));
    this.addToCartButton = this.querySelector("[data-pack-add-to-cart]");
    this.stockMessage = this.querySelector("[data-pack-stock-message]");

    this.addEventListener("click", this.onClick.bind(this));
    this.addEventListener("keydown", (event) => {
      if (!["Enter", " "].includes(event.key)) return;
      const size = event.target.closest(".product-pack__size");
      if (!size) return;
      event.preventDefault();
      size.click();
    });

    this.refresh();

    // Same deferral as VariantSelects so the gallery markup and Embla are ready
    setTimeout(() => this.updateGallery(), 100);
  }

  getPackProductData() {
    const script = this.querySelector("[data-pack-product-json]");
    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error("ProductPackSelector: Failed to parse product JSON", error);
      return null;
    }
  }

  onClick(event) {
    const tier = event.target.closest("[data-pack-size]");
    if (tier) {
      this.setPackSize(Number(tier.dataset.packSize));
      return;
    }

    const swatch = event.target.closest("[data-pack-colour-row] .color-swatch");
    if (swatch) {
      event.preventDefault();
      this.selectColour(swatch);
      return;
    }

    const size = event.target.closest(".product-pack__size");
    if (size) {
      this.selectSize(size);
      return;
    }

    // Stop here: product-card-interactions.js listens on document for any .add-to-cart-button
    // and throws when the button isn't inside a product card
    if (event.target.closest("[data-pack-add-to-cart]")) {
      event.stopPropagation();
      this.addToCart();
      return;
    }

    if (event.target.closest("[data-pack-buy-now]")) {
      event.stopPropagation();
      this.buyNow(event);
    }
  }

  setPackSize(packSize) {
    if (!packSize || packSize === this.packSize) return;
    this.packSize = packSize;
    this.refresh();

    // The tee the gallery was showing is no longer in the pack — fall back to Tee 1
    if (this.galleryRowIndex >= this.packSize) {
      this.galleryRowIndex = 0;
      this.updateGallery();
    }
  }

  selectColour(swatch) {
    if (swatch.getAttribute("aria-disabled") === "true") return;

    const row = swatch.closest("[data-pack-colour-row]");
    row.querySelectorAll(".color-swatch").forEach((item) => {
      const isActive = item === swatch;
      item.classList.toggle("active", isActive);
      item.setAttribute("aria-pressed", String(isActive));
    });

    this.refresh();

    // Gallery follows whichever tee the customer last changed
    this.galleryRowIndex = this.rows.indexOf(row);
    this.updateGallery();
  }

  selectSize(option) {
    if (option.getAttribute("aria-disabled") === "true") {
      this.openNotifyMe(option.dataset.optionValue);
      return;
    }

    this.selectedSize = option.dataset.optionValue;
    this.hideSizeNotSelectedMessage();

    if (typeof window.updateSizeChart === "function") {
      window.updateSizeChart(this.selectedSize);
    }

    this.refresh();
  }

  // Same as the default template: a sold-out size opens notify-me for the colour that can't be filled
  openNotifyMe(size) {
    if (typeof window.openNotifyMeModal !== "function") return;

    const counts = this.countColours(this.getSelectedColours());
    const soldOutColour = Object.keys(counts).find(
      (colour) => !this.hasStock(this.findPackVariant(colour, size), counts[colour])
    );
    const variant = soldOutColour && this.findPackVariant(soldOutColour, size);
    if (variant) window.openNotifyMeModal(String(variant.id), size);
  }

  /* ---------- State helpers ---------- */

  getActiveRows() {
    return this.rows.slice(0, this.packSize);
  }

  getRowColour(row) {
    return row?.querySelector(".color-swatch.active")?.dataset.colorValue || null;
  }

  getSelectedColours() {
    return this.getActiveRows().map((row) => this.getRowColour(row));
  }

  findPackVariant(colour, size) {
    return this.productData.variants.find(
      (variant) =>
        (this.colorIndex < 0 || variant.options[this.colorIndex] === colour) &&
        (this.sizeIndex < 0 || variant.options[this.sizeIndex] === size)
    );
  }

  findPriceVariant(colour) {
    if (this.selectedSize) return this.findPackVariant(colour, this.selectedSize);

    return this.productData.variants.find(
      (variant) => this.colorIndex < 0 || variant.options[this.colorIndex] === colour
    );
  }

  hasStock(variant, quantity) {
    if (!variant || !variant.available) return false;
    if (!variant.inventory_tracked) return true;
    return variant.inventory_quantity >= quantity;
  }

  countColours(colours) {
    return colours.reduce((counts, colour) => {
      counts[colour] = (counts[colour] || 0) + 1;
      return counts;
    }, {});
  }

  isComboAvailable(colours, size) {
    return Object.entries(this.countColours(colours)).every(([colour, quantity]) =>
      this.hasStock(this.findPackVariant(colour, size), quantity)
    );
  }

  getSizeValues() {
    return Array.from(this.querySelectorAll(".product-pack__size")).map(
      (option) => option.dataset.optionValue
    );
  }

  /* ---------- Rendering ---------- */

  refresh() {
    this.renderTiers();
    this.renderRows();
    this.renderSwatchAvailability();
    this.renderSizes();
    this.renderPrice();
    this.renderButtons();
  }

  renderTiers() {
    this.querySelectorAll("[data-pack-size]").forEach((tier) => {
      const isActive = Number(tier.dataset.packSize) === this.packSize;
      tier.classList.toggle("is-active", isActive);
      tier.setAttribute("aria-checked", String(isActive));
    });

    const unitLabel =
      this.packSize === 1 ? this.dataset.unitLabel : this.dataset.unitLabelPlural;
    const quantityLabel = this.querySelector("[data-pack-quantity-label]");
    if (quantityLabel) quantityLabel.textContent = `${this.packSize} ${unitLabel}`;
  }

  renderRows() {
    const isPack = this.packSize > 1;

    this.rows.forEach((row, index) => {
      row.hidden = index >= this.packSize;
      const value = row.querySelector("[data-pack-row-value]");
      if (value) value.textContent = this.getRowColour(row) || "";
    });

    const heading = this.querySelector("[data-pack-colours-heading]");
    if (heading) {
      heading.textContent = isPack ? heading.dataset.headingMulti : heading.dataset.headingSingle;
    }

    const coloursHint = this.querySelector("[data-pack-colours-hint]");
    if (coloursHint) coloursHint.hidden = !isPack;

    const sizeHint = this.querySelector("[data-pack-size-hint]");
    if (sizeHint) sizeHint.hidden = !isPack;

    this.classList.toggle("is-single", !isPack);
  }

  renderSwatchAvailability() {
    const activeRows = this.getActiveRows();
    const selectedColours = this.getSelectedColours();
    const sizes = this.selectedSize ? [this.selectedSize] : this.getSizeValues();

    activeRows.forEach((row, rowIndex) => {
      const otherColours = selectedColours.filter((_, index) => index !== rowIndex);

      row.querySelectorAll(".color-swatch").forEach((swatch) => {
        const colour = swatch.dataset.colorValue;
        const quantity = otherColours.filter((item) => item === colour).length + 1;
        const isAvailable = sizes.some((size) =>
          this.hasStock(this.findPackVariant(colour, size), quantity)
        );

        swatch.classList.toggle("sold-out", !isAvailable);
        swatch.setAttribute("aria-disabled", String(!isAvailable));
      });
    });
  }

  renderSizes() {
    const colours = this.getSelectedColours();

    this.querySelectorAll(".product-pack__size").forEach((option) => {
      const size = option.dataset.optionValue;
      const isAvailable = this.isComboAvailable(colours, size);
      const isActive = size === this.selectedSize;

      option.classList.toggle("active", isActive);
      option.classList.toggle("sold-out", !isAvailable);
      option.setAttribute("aria-pressed", String(isActive));
      option.setAttribute("aria-disabled", String(!isAvailable));
    });

    const sizeValue = this.querySelector("[data-pack-size-value]");
    if (sizeValue) sizeValue.textContent = this.selectedSize ? `: ${this.selectedSize}` : "";
  }

  renderPrice() {
    const variants = this.getSelectedColours().map((colour) => this.findPriceVariant(colour));
    if (variants.some((variant) => !variant)) return;

    const total = this.getPackTotal(variants.map((variant) => variant.price));
    const compareTotal = variants.reduce(
      (sum, variant) => sum + Math.max(variant.compare_at_price || 0, variant.price),
      0
    );
    const saving = compareTotal - total;

    const priceEl = this.productInfo?.querySelector("[data-pack-price]");
    const compareEl = this.productInfo?.querySelector("[data-pack-compare-price]");

    if (priceEl) priceEl.textContent = this.formatMoney(total);
    if (compareEl) {
      compareEl.textContent = this.formatMoney(compareTotal);
      compareEl.hidden = saving <= 0;
    }
  }

  // Mirrors the automatic discount: percentage off, fixed rupees off each tee, or fixed rupees off the whole pack
  getPackTotal(prices) {
    const fullTotal = prices.reduce((sum, price) => sum + price, 0);
    const discount = this.discounts[this.packSize] || 0;

    if (this.discountType === "pack_fixed") {
      return Math.max(fullTotal - Math.round(discount * 100), 0);
    }

    return prices.reduce((sum, price) => sum + this.getPackUnitPrice(price), 0);
  }

  getPackUnitPrice(price) {
    const discount = this.discounts[this.packSize] || 0;
    if (!discount) return price;

    if (this.discountType === "percentage") {
      return Math.round((price * Math.max(100 - discount, 0)) / 100);
    }

    return Math.max(price - Math.round(discount * 100), 0);
  }

  renderButtons() {
    if (!this.addToCartButton) return;

    const canBuy = !this.selectedSize || this.isComboAvailable(this.getSelectedColours(), this.selectedSize);
    const { label, labelSoldOut } = this.addToCartButton.dataset;

    this.addToCartButton.textContent = canBuy ? label : labelSoldOut;
    this.addToCartButton.classList.toggle("unavailable", !canBuy);
    this.addToCartButton.setAttribute("aria-disabled", String(!canBuy));

    if (this.stockMessage) {
      this.stockMessage.hidden = canBuy;
      if (!canBuy) {
        this.stockMessage.textContent =
          "Not enough stock for this combination. Try another colour or size.";
      }
    }
  }

  updateGallery() {
    const colour = this.getRowColour(this.rows[this.galleryRowIndex]);
    if (colour && typeof this.updateGalleryForColor === "function") {
      this.updateGalleryForColor(colour);
    }
  }

  /* ---------- Cart ---------- */

  validateSelection() {
    if (this.sizeIndex >= 0 && !this.selectedSize) {
      this.showSizeNotSelectedMessage();
      this.querySelector(".product-pack__group--size")?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return false;
    }

    return this.isComboAvailable(this.getSelectedColours(), this.selectedSize);
  }

  buildItems() {
    const counts = this.countColours(this.getSelectedColours());
    const properties =
      this.packSize > 1
        ? {
            _pack_id: `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
            _pack_size: String(this.packSize),
          }
        : null;

    return Object.entries(counts).map(([colour, quantity]) => {
      const variant = this.findPackVariant(colour, this.selectedSize);
      const item = { id: variant.id, quantity };
      if (properties) item.properties = properties;
      return item;
    });
  }

  exceedsVariantLimit(cart, items) {
    return items.some((item) => {
      const inCart = cart.items
        .filter((line) => Number(line.variant_id) === item.id)
        .reduce((sum, line) => sum + (line.quantity || 0), 0);
      return inCart + item.quantity > PACK_MAX_PER_VARIANT;
    });
  }

  showStockMessage(message) {
    if (!this.stockMessage) return;
    this.stockMessage.textContent = message;
    this.stockMessage.hidden = false;
  }

  async addToCart() {
    if (this.loading || !this.validateSelection()) return;

    const items = this.buildItems();
    this.loading = true;
    this.addToCartButton.classList.add("is-loading");

    try {
      const currentCart = await fetch("/cart.js").then((response) => response.json());

      if (this.exceedsVariantLimit(currentCart, items)) {
        this.showStockMessage(
          `Maximum quantity for a colour and size is ${PACK_MAX_PER_VARIANT}. Check your cart.`
        );
        window.dispatchEvent(new CustomEvent("cart:open"));
        return;
      }

      const freebieVariantId = Number(window.FREEBIE_CONFIG?.variantId) || null;
      const hasFreebie = currentCart.items.some(
        (line) => Number(line.variant_id) === freebieVariantId
      );
      const itemsToAdd =
        window.FREEBIE_CONFIG?.enabled && freebieVariantId && !hasFreebie
          ? [...items, { id: freebieVariantId, quantity: 1 }]
          : items;

      window.dispatchEvent(new CustomEvent("cart:loading"));

      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          items: itemsToAdd,
          sections: ["cart-drawer"],
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        this.showStockMessage(data.description || data.message || "Could not add to cart.");
        return;
      }

      const cart = await fetch("/cart.js").then((res) => res.json());

      window.dispatchEvent(
        new CustomEvent("cart:count-update", { detail: { count: cart.item_count } })
      );
      window.dispatchEvent(
        new CustomEvent("cart:updated", { detail: { cart, item: data, sections: data.sections } })
      );
      document.dispatchEvent(new CustomEvent("cart:item-added", { detail: { cart, item: data } }));
      window.dispatchEvent(new CustomEvent("cart:open"));
    } catch (error) {
      console.error("Pack add to cart failed:", error);
      this.showStockMessage("Could not add to cart. Please try again.");
    } finally {
      this.loading = false;
      this.addToCartButton.classList.remove("is-loading");
      window.dispatchEvent(new CustomEvent("cart:loaded"));
    }
  }

  buyNow(event) {
    if (!this.validateSelection()) return;

    const items = this.buildItems().map(({ id, quantity }) => ({ id, quantity }));

    if (typeof window.onBuyNowClick === "function") {
      window.onBuyNowClick(event, false, items);
      return;
    }

    window.location.href = `/cart/${items.map((item) => `${item.id}:${item.quantity}`).join(",")}`;
  }

  /* ---------- Money ---------- */

  formatMoney(cents) {
    const format = this.dataset.moneyFormat || "₹{{amount_no_decimals}}";
    const amount = (cents / 100).toFixed(2);
    const [whole, decimals] = amount.split(".");
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    const trimmed = decimals === "00" ? withCommas : `${withCommas}.${decimals}`;

    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, placeholder) => {
      switch (placeholder) {
        case "amount_no_decimals":
          return withCommas;
        case "amount_with_comma_separator":
          return trimmed.replace(/,/g, ".").replace(/\.(\d{2})$/, ",$1");
        default:
          return trimmed;
      }
    });
  }
}

if (!customElements.get("product-pack-selector")) {
  customElements.define("product-pack-selector", ProductPackSelector);
}
